import React from "react";
import { motion, useReducedMotion } from "framer-motion";

const PALETTE = {
  blue: "#1687F3",
  blueDark: "#075FBF",
  blueSoft: "#87C9F5",
  teal: "#0AA39A",
  amber: "#D58A32",
  violet: "#8171D8",
  coral: "#D96C63",
};

const EASE_OUT = [0.16, 1, 0.3, 1];

const monoLabel = {
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.12em",
};

const monoValue = {
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: 10,
  fontWeight: 600,
};

const fadeIn = (reducedMotion, delay = 0, distance = 6) => ({
  initial: reducedMotion ? false : { opacity: 0, y: distance },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: reducedMotion ? 0 : 0.56,
    delay: reducedMotion ? 0 : delay,
    ease: EASE_OUT,
  },
});

const fadeOpacity = (reducedMotion, delay = 0) => ({
  initial: reducedMotion ? false : { opacity: 0 },
  animate: { opacity: 1 },
  transition: {
    duration: reducedMotion ? 0 : 0.42,
    delay: reducedMotion ? 0 : delay,
    ease: EASE_OUT,
  },
});

const drawIn = (reducedMotion, delay = 0, duration = 0.9) => ({
  initial: reducedMotion ? false : { opacity: 0, pathLength: 0 },
  animate: { opacity: 1, pathLength: 1 },
  transition: {
    duration: reducedMotion ? 0 : duration,
    delay: reducedMotion ? 0 : delay,
    ease: EASE_OUT,
  },
});

const visualClassName =
  "h-auto w-full text-slate-950 dark:text-white";

const CELL_OBJECTS = [
  [66, 82, 15, 12, -2, 1, -8],
  [119, 76, 12, 14, 1, -2, 11],
  [171, 91, 16, 13, -1, 1, -4],
  [228, 75, 13, 11, 2, 0, 8],
  [279, 91, 17, 13, -2, -1, -11],
  [339, 77, 14, 12, 1, 1, 5],
  [390, 94, 13, 15, -1, -2, 14],
  [91, 139, 16, 12, 2, 1, 7],
  [145, 132, 13, 11, -2, -1, -6, true],
  [204, 146, 18, 14, 1, 0, 4],
  [261, 130, 13, 15, -1, 2, -12],
  [320, 145, 15, 12, 2, -1, 10],
  [377, 137, 17, 13, -2, 1, -3],
  [57, 194, 13, 11, 1, 0, 9],
  [116, 190, 17, 14, -2, -1, -6],
  [178, 205, 14, 12, 1, 2, 12],
  [237, 187, 18, 13, -1, 0, -7],
  [296, 203, 13, 15, 2, -1, 5],
  [352, 187, 16, 12, -2, 1, -10],
  [402, 207, 12, 14, 0, -2, 8],
  [82, 252, 17, 13, 2, 0, -5],
  [143, 239, 13, 11, -1, 1, 7, true],
  [205, 258, 15, 14, 1, -2, -8],
  [266, 240, 18, 13, -2, 1, 4],
  [325, 256, 14, 12, 2, 0, 11],
  [383, 244, 16, 14, -1, -1, -6],
  [109, 296, 14, 11, -1, 1, 5],
  [180, 292, 17, 13, 2, -1, -9],
  [250, 301, 13, 12, -2, 0, 7],
  [326, 292, 17, 14, 1, 1, -5],
  [394, 299, 14, 12, -1, -1, 10],
];

const CELL_AREA_BINS = [16, 30, 49, 72, 92, 78, 51, 27, 12];

export function CellAssayVisual({ className = "" }) {
  const reducedMotion = useReducedMotion();

  return (
    <svg
      viewBox="0 0 640 400"
      className={`${visualClassName} ${className}`}
      role="img"
      aria-label="Microscopy segmentation field with cell morphology distribution and quality-control metrics"
      focusable="false"
    >
      <defs>
        <pattern id="cell-field-grid" width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M28 0H0V28" fill="none" stroke="currentColor" strokeOpacity="0.045" strokeWidth="0.8" />
        </pattern>
        <clipPath id="cell-field-clip">
          <rect x="28" y="48" width="394" height="284" rx="16" />
        </clipPath>
      </defs>

      <rect x="1" y="1" width="638" height="398" rx="24" className="fill-[#f7faff] stroke-slate-300 dark:fill-[#07090d] dark:stroke-white/[0.12]" />
      <text x="28" y="29" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">
        LIVE CELL QC / SEGMENTATION
      </text>
      <text x="611" y="29" textAnchor="end" {...monoValue} className="fill-slate-800 dark:fill-slate-100">
        FIELD 07 · 20×
      </text>

      <rect x="28" y="48" width="394" height="284" rx="16" className="fill-white stroke-slate-300 dark:fill-[#0a0f16] dark:stroke-white/[0.1]" />
      <rect x="28" y="48" width="394" height="284" rx="16" fill="url(#cell-field-grid)" />

      <g clipPath="url(#cell-field-clip)">
        <path d="M33 219C101 176 151 220 209 184S329 139 418 178" fill="none" stroke={PALETTE.blue} strokeOpacity="0.09" strokeWidth="32" />
        {CELL_OBJECTS.map((cell, index) => {
          const [x, y, rx, ry, nucleusX, nucleusY, rotation, flagged] = cell;
          const color = flagged ? PALETTE.amber : PALETTE.blue;
          return (
            <motion.g
              key={`${x}-${y}`}
              initial={reducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: reducedMotion ? 0 : 0.45,
                delay: reducedMotion ? 0 : 0.08 + (index % 10) * 0.035,
                ease: EASE_OUT,
              }}
            >
              <g transform={`rotate(${rotation} ${x} ${y})`}>
                <ellipse cx={x} cy={y} rx={rx} ry={ry} fill={color} fillOpacity={flagged ? 0.13 : 0.1} />
                <motion.ellipse
                  cx={x}
                  cy={y}
                  rx={rx}
                  ry={ry}
                  fill="none"
                  stroke={color}
                  strokeOpacity={flagged ? 0.9 : 0.7}
                  strokeWidth={flagged ? 1.7 : 1.25}
                  vectorEffect="non-scaling-stroke"
                  {...drawIn(reducedMotion, 0.16 + (index % 8) * 0.035, 0.48)}
                />
                <ellipse
                  cx={x + nucleusX}
                  cy={y + nucleusY}
                  rx={Math.max(3.2, rx * 0.28)}
                  ry={Math.max(3, ry * 0.3)}
                  fill={color}
                  fillOpacity={flagged ? 0.8 : 0.62}
                />
              </g>
            </motion.g>
          );
        })}

        <motion.g {...fadeIn(reducedMotion, 0.62, 0)}>
          <path d="M143 117V105H181" fill="none" stroke={PALETTE.amber} strokeWidth="1" />
          <text x="184" y="108" {...monoLabel} fill={PALETTE.amber}>FLAG / EDGE</text>
          <path d="M237 170V157H273" fill="none" stroke={PALETTE.blue} strokeWidth="1" />
          <text x="277" y="160" {...monoLabel} fill={PALETTE.blue}>OBJ 642</text>
        </motion.g>
      </g>

      <g className="text-slate-500 dark:text-slate-400">
        <path d="M44 312H92" stroke="currentColor" strokeWidth="2" />
        <text x="99" y="315" {...monoLabel} fill="currentColor">50 µm</text>
      </g>

      <rect x="440" y="48" width="172" height="284" rx="16" className="fill-white stroke-slate-300 dark:fill-[#0a0f16] dark:stroke-white/[0.1]" />
      <text x="458" y="74" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">CELL AREA / µm²</text>
      <g>
        <path d="M458 170H594" className="stroke-slate-300 dark:stroke-white/[0.12]" strokeWidth="1" />
        {CELL_AREA_BINS.map((value, index) => {
          const barHeight = value * 0.78;
          return (
            <motion.rect
              key={value + index}
              x={461 + index * 14.4}
              y={170 - barHeight}
              width="9"
              height={barHeight}
              rx="2"
              fill={index === 4 ? PALETTE.blue : PALETTE.blueSoft}
              fillOpacity={index === 4 ? 0.95 : 0.58}
              {...fadeIn(reducedMotion, 0.22 + index * 0.035, 3)}
            />
          );
        })}
        <motion.path
          d="M521 89V175"
          stroke={PALETTE.blueDark}
          strokeWidth="1.2"
          strokeDasharray="3 3"
          {...drawIn(reducedMotion, 0.56, 0.48)}
        />
        <text x="526" y="99" {...monoLabel} fill={PALETTE.blueDark}>MEDIAN 184</text>
      </g>

      <g transform="translate(458 196)">
        {[
          ["CONFLUENCE", "72%"],
          ["VIABLE", "94%"],
          ["OBJECTS", "1,284"],
        ].map(([label, value], index) => (
          <motion.g key={label} transform={`translate(0 ${index * 36})`} {...fadeOpacity(reducedMotion, 0.46 + index * 0.08)}>
            <text x="0" y="0" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">{label}</text>
            <text x="136" y="0" textAnchor="end" {...monoValue} className="fill-slate-900 dark:fill-white">{value}</text>
            <path d="M0 12H136" className="stroke-slate-200 dark:stroke-white/[0.08]" />
          </motion.g>
        ))}
      </g>

      <motion.g {...fadeIn(reducedMotion, 0.78, 0)}>
        <rect x="458" y="296" width="136" height="20" rx="10" fill={PALETTE.blue} fillOpacity="0.1" stroke={PALETTE.blue} strokeOpacity="0.4" />
        <circle cx="469" cy="306" r="3" fill={PALETTE.blue} />
        <text x="478" y="309" {...monoLabel} fill={PALETTE.blueDark} className="dark:fill-[#58b2ff]">MASK QC · PASSED</text>
      </motion.g>

      <text x="28" y="370" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">
        SEGMENTED OBJECTS · MORPHOLOGY DISTRIBUTION · OUTLIER REVIEW
      </text>
      <text x="612" y="370" textAnchor="end" {...monoLabel} fill={PALETTE.blue}>
        TRACEABLE IMAGE 01/01
      </text>
    </svg>
  );
}

const IPSC_STAGES = [
  { x: 84, y: 114, day: "D0", stage: "PLURIPOTENT", value: "QC 0.94", type: "colony" },
  { x: 228, y: 171, day: "D7", stage: "LINEAGE GATE", value: "QC 0.76", type: "rosette" },
  { x: 400, y: 132, day: "D14", stage: "MATURATION", value: "QC 0.88", type: "elongated" },
  { x: 556, y: 91, day: "D21", stage: "PHENOTYPE", value: "QC 0.96", type: "network" },
];

function IpscMorphology({ stage, reducedMotion, index }) {
  const { x, y, type } = stage;
  const common = fadeIn(reducedMotion, 0.24 + index * 0.16, 5);

  if (type === "colony") {
    return (
      <motion.g {...common}>
        <circle cx={x} cy={y} r="28" fill={PALETTE.blue} fillOpacity="0.08" stroke={PALETTE.blue} strokeOpacity="0.35" />
        {[
          [-10, -6], [0, -10], [10, -5], [-12, 5], [-2, 3], [9, 6], [0, 13],
        ].map(([dx, dy], cellIndex) => (
          <circle key={cellIndex} cx={x + dx} cy={y + dy} r="6.5" fill={PALETTE.blue} fillOpacity={0.55 + (cellIndex % 2) * 0.16} />
        ))}
      </motion.g>
    );
  }

  if (type === "rosette") {
    return (
      <motion.g {...common}>
        <circle cx={x} cy={y} r="28" fill={PALETTE.violet} fillOpacity="0.07" stroke={PALETTE.violet} strokeOpacity="0.42" />
        {Array.from({ length: 8 }, (_, cellIndex) => {
          const angle = (Math.PI * 2 * cellIndex) / 8;
          return (
            <ellipse
              key={cellIndex}
              cx={x + Math.cos(angle) * 13}
              cy={y + Math.sin(angle) * 13}
              rx="7"
              ry="4"
              transform={`rotate(${(angle * 180) / Math.PI + 90} ${x + Math.cos(angle) * 13} ${y + Math.sin(angle) * 13})`}
              fill={PALETTE.violet}
              fillOpacity="0.62"
            />
          );
        })}
        <circle cx={x} cy={y} r="4" fill={PALETTE.violet} />
      </motion.g>
    );
  }

  if (type === "elongated") {
    return (
      <motion.g {...common}>
        <circle cx={x} cy={y} r="28" fill={PALETTE.teal} fillOpacity="0.06" stroke={PALETTE.teal} strokeOpacity="0.42" />
        {[-16, -5, 7, 18].map((offset, cellIndex) => (
          <ellipse
            key={offset}
            cx={x + offset}
            cy={y + (cellIndex % 2 ? 7 : -6)}
            rx="10"
            ry="4"
            transform={`rotate(${cellIndex % 2 ? -28 : 25} ${x + offset} ${y + (cellIndex % 2 ? 7 : -6)})`}
            fill={PALETTE.teal}
            fillOpacity="0.65"
          />
        ))}
      </motion.g>
    );
  }

  return (
    <motion.g {...common}>
      <circle cx={x} cy={y} r="28" fill={PALETTE.blue} fillOpacity="0.06" stroke={PALETTE.blue} strokeOpacity="0.42" />
      <circle cx={x} cy={y} r="8" fill={PALETTE.blue} fillOpacity="0.8" />
      {[
        [-23, -16], [20, -18], [-25, 13], [24, 12], [2, -26], [5, 27],
      ].map(([dx, dy], branchIndex) => (
        <g key={branchIndex}>
          <path d={`M${x} ${y}L${x + dx} ${y + dy}`} stroke={PALETTE.blue} strokeWidth="1.4" />
          <circle cx={x + dx} cy={y + dy} r="3" fill={PALETTE.blue} />
        </g>
      ))}
    </motion.g>
  );
}

export function IpscTrajectoryVisual({ className = "" }) {
  const reducedMotion = useReducedMotion();

  return (
    <svg
      viewBox="0 0 640 400"
      className={`${visualClassName} ${className}`}
      role="img"
      aria-label="iPSC differentiation trajectory across four quality-control stage gates from pluripotency to mature phenotype"
      focusable="false"
    >
      <defs>
        <linearGradient id="ipsc-confidence-fill" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={PALETTE.blue} stopOpacity="0.14" />
          <stop offset="0.45" stopColor={PALETTE.violet} stopOpacity="0.12" />
          <stop offset="1" stopColor={PALETTE.teal} stopOpacity="0.14" />
        </linearGradient>
      </defs>

      <rect x="1" y="1" width="638" height="398" rx="24" className="fill-[#f7faff] stroke-slate-300 dark:fill-[#07090d] dark:stroke-white/[0.12]" />
      <text x="28" y="29" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">DIFFERENTIATION TRAJECTORY / BATCH IP-042</text>
      <text x="612" y="29" textAnchor="end" {...monoValue} fill={PALETTE.teal}>3 / 3 GATES PASSED</text>

      <rect x="28" y="48" width="584" height="286" rx="16" className="fill-white stroke-slate-300 dark:fill-[#0a0f16] dark:stroke-white/[0.1]" />

      {[0.5, 0.7, 0.9].map((tick, index) => {
        const y = 276 - index * 84;
        return (
          <g key={tick} className="text-slate-400 dark:text-slate-500">
            <path d={`M56 ${y}H590`} stroke="currentColor" strokeWidth="1" strokeDasharray="2 5" />
            <text x="48" y={y + 3} textAnchor="end" {...monoLabel} fill="currentColor">{tick.toFixed(1)}</text>
          </g>
        );
      })}
      <text x="47" y="72" textAnchor="end" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">QC</text>

      <path d="M56 210H590" stroke={PALETTE.amber} strokeOpacity="0.55" strokeDasharray="5 5" />
      <text x="587" y="203" textAnchor="end" {...monoLabel} fill={PALETTE.amber}>MIN ACCEPTANCE 0.70</text>

      <path
        d="M84 89C138 96 174 174 228 194S338 153 400 154S497 82 556 68L556 113C501 124 458 166 400 163S287 206 228 201S136 130 84 139Z"
        fill="url(#ipsc-confidence-fill)"
      />
      <motion.path
        d="M84 114C139 112 174 177 228 171S337 140 400 132S495 99 556 91"
        fill="none"
        stroke={PALETTE.blue}
        strokeWidth="2.5"
        strokeLinecap="round"
        {...drawIn(reducedMotion, 0.12, 1.15)}
      />

      {IPSC_STAGES.map((stage, index) => (
        <g key={stage.day}>
          <path d={`M${stage.x} ${stage.y + 30}V305`} className="stroke-slate-300 dark:stroke-white/[0.1]" strokeDasharray="2 5" />
          <IpscMorphology stage={stage} reducedMotion={reducedMotion} index={index} />
          <motion.circle
            cx={stage.x}
            cy={stage.y}
            r="31"
            fill="none"
            stroke={index === 3 ? PALETTE.teal : PALETTE.blue}
            strokeOpacity="0.26"
            {...drawIn(reducedMotion, 0.32 + index * 0.16, 0.52)}
          />
          <motion.g {...fadeIn(reducedMotion, 0.42 + index * 0.14, 4)}>
            <text x={stage.x} y="351" textAnchor="middle" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">{stage.day}</text>
            <text x={stage.x} y="368" textAnchor="middle" {...monoLabel} className="fill-slate-800 dark:fill-slate-100">{stage.stage}</text>
            <text x={stage.x} y="384" textAnchor="middle" {...monoValue} fill={index === 3 ? PALETTE.teal : PALETTE.blue}>{stage.value}</text>
          </motion.g>
        </g>
      ))}

      <text x="42" y="322" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">CONFIDENCE</text>
      <text x="594" y="322" textAnchor="end" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">PROCESS DAY →</text>
    </svg>
  );
}

const SCREEN_ROWS = ["A", "B", "C", "D", "E", "F", "G", "H"];
const SCREEN_HITS = new Map([
  ["B04", -1.8],
  ["C09", 1.62],
  ["E06", -2.05],
  ["F11", 1.74],
  ["H03", -1.57],
]);
const SCREEN_CONTROLS = new Map([
  ["A01", { type: "NTC", score: -0.04 }],
  ["A02", { type: "NTC", score: 0.07 }],
  ["A11", { type: "POS", score: -2.21 }],
  ["A12", { type: "POS", score: -2.08 }],
]);

const wellId = (row, column) => `${SCREEN_ROWS[row]}${String(column + 1).padStart(2, "0")}`;

const screenScore = (row, column) => {
  const id = wellId(row, column);
  if (SCREEN_HITS.has(id)) return SCREEN_HITS.get(id);
  if (SCREEN_CONTROLS.has(id)) return SCREEN_CONTROLS.get(id).score;
  const index = row * 12 + column;
  return ((((index * 17) % 19) - 9) / 15) * (index % 5 === 0 ? 0.7 : 1);
};

const scoreFillClass = (score) => {
  if (score <= -1.25) return "fill-[#075fbf] dark:fill-[#38a3ff]";
  if (score <= -0.45) return "fill-[#88c8f2] dark:fill-[#17699e]";
  if (score < 0.45) return "fill-[#dce7f1] dark:fill-[#27313e]";
  if (score < 1.25) return "fill-[#efc882] dark:fill-[#986721]";
  return "fill-[#d77b2f] dark:fill-[#f1a65d]";
};

export function SirnaScreenVisual({ className = "" }) {
  const reducedMotion = useReducedMotion();
  const gridX = 78;
  const gridY = 80;
  const columnGap = 34;
  const rowGap = 30;

  return (
    <svg
      viewBox="0 0 640 400"
      className={`${visualClassName} ${className}`}
      role="img"
      aria-label="Exact 12 by 8 siRNA screen heatmap with non-targeting and positive controls, normalized effects, and five labeled hits"
      focusable="false"
    >
      <rect x="1" y="1" width="638" height="398" rx="24" className="fill-[#f7faff] stroke-slate-300 dark:fill-[#07090d] dark:stroke-white/[0.12]" />
      <text x="28" y="29" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">siRNA SCREEN / NORMALIZED PHENOTYPE</text>
      <text x="612" y="29" textAnchor="end" {...monoValue} fill={PALETTE.blue}>PLATE S-184 · QC PASS</text>

      <rect x="42" y="48" width="440" height="292" rx="18" className="fill-white stroke-slate-300 dark:fill-[#0a0f16] dark:stroke-white/[0.1]" />
      <rect x="54" y="61" width="416" height="264" rx="14" className="fill-slate-50 stroke-slate-200 dark:fill-[#080b10] dark:stroke-white/[0.07]" />

      {Array.from({ length: 12 }, (_, column) => (
        <text
          key={column}
          x={gridX + column * columnGap}
          y="71"
          textAnchor="middle"
          {...monoLabel}
          className="fill-slate-500 dark:fill-slate-400"
        >
          {String(column + 1).padStart(2, "0")}
        </text>
      ))}

      {SCREEN_ROWS.map((rowLabel, row) => (
        <motion.g key={rowLabel} {...fadeIn(reducedMotion, 0.08 + row * 0.055, 3)}>
          <text
            x="62"
            y={gridY + row * rowGap + 3}
            textAnchor="middle"
            {...monoLabel}
            className="fill-slate-500 dark:fill-slate-400"
          >
            {rowLabel}
          </text>
          {Array.from({ length: 12 }, (_, column) => {
            const id = wellId(row, column);
            const score = screenScore(row, column);
            const hit = SCREEN_HITS.has(id);
            const control = SCREEN_CONTROLS.get(id);
            const cx = gridX + column * columnGap;
            const cy = gridY + row * rowGap;
            return (
              <g key={id}>
                <circle cx={cx} cy={cy} r="10" className="fill-white stroke-slate-300 dark:fill-[#0d121a] dark:stroke-white/[0.1]" />
                <circle cx={cx} cy={cy} r="7.6" className={scoreFillClass(score)} />
                {control && (
                  <circle
                    cx={cx}
                    cy={cy}
                    r="12.4"
                    fill="none"
                    stroke={control.type === "POS" ? PALETTE.amber : PALETTE.teal}
                    strokeWidth="1.3"
                    strokeDasharray="2.5 2.5"
                  />
                )}
                {hit && (
                  <motion.circle
                    cx={cx}
                    cy={cy}
                    r="12.5"
                    fill="none"
                    stroke={score < 0 ? PALETTE.blueDark : PALETTE.amber}
                    strokeWidth="2"
                    {...drawIn(reducedMotion, 0.58 + row * 0.035 + column * 0.015, 0.55)}
                  />
                )}
              </g>
            );
          })}
        </motion.g>
      ))}

      <g transform="translate(500 52)">
        <text x="0" y="14" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">SCREEN QC</text>
        {[
          ["Z′ FACTOR", "0.71"],
          ["PLATE CV", "6.2%"],
          ["HITS", "5 / 96"],
        ].map(([label, value], index) => (
          <motion.g key={label} transform={`translate(0 ${35 + index * 38})`} {...fadeOpacity(reducedMotion, 0.36 + index * 0.08)}>
            <text x="0" y="0" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">{label}</text>
            <text x="112" y="0" textAnchor="end" {...monoValue} className="fill-slate-900 dark:fill-white">{value}</text>
            <path d="M0 12H112" className="stroke-slate-200 dark:stroke-white/[0.08]" />
          </motion.g>
        ))}

        <text x="0" y="164" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">TOP HITS / Z</text>
        {[
          ["E06", "−2.05"],
          ["B04", "−1.80"],
          ["F11", "+1.74"],
        ].map(([id, score], index) => (
          <motion.g key={id} transform={`translate(0 ${186 + index * 24})`} {...fadeOpacity(reducedMotion, 0.62 + index * 0.07)}>
            <circle cx="4" cy="-3" r="3.2" fill={index === 2 ? PALETTE.amber : PALETTE.blue} />
            <text x="14" y="0" {...monoValue} className="fill-slate-800 dark:fill-slate-100">{id}</text>
            <text x="112" y="0" textAnchor="end" {...monoValue} fill={index === 2 ? PALETTE.amber : PALETTE.blue}>{score}</text>
          </motion.g>
        ))}
      </g>

      <g transform="translate(54 358)">
        <text x="0" y="7" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">EFFECT Z</text>
        {[
          ["≤ −1.25", "#075fbf"],
          ["−0.45", "#88c8f2"],
          ["0", "#dce7f1"],
          ["+0.45", "#efc882"],
          ["≥ +1.25", "#d77b2f"],
        ].map(([label, color], index) => (
          <g key={label} transform={`translate(${72 + index * 70} 0)`}>
            <rect x="0" y="-3" width="16" height="12" rx="3" fill={color} />
            <text x="21" y="7" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">{label}</text>
          </g>
        ))}
      </g>
      <g transform="translate(500 346)">
        <circle cx="4" cy="4" r="6" fill="none" stroke={PALETTE.teal} strokeDasharray="2 2" />
        <text x="16" y="7" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">NTC A01–02</text>
        <circle cx="4" cy="25" r="6" fill="none" stroke={PALETTE.amber} strokeDasharray="2 2" />
        <text x="16" y="28" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">POS A11–12</text>
      </g>
    </svg>
  );
}

const ANTIBODIES = [
  { id: "AB-01", ec50: 0.32, max: 96, hill: 1.08, color: PALETTE.blue, dash: undefined },
  { id: "AB-02", ec50: 1.7, max: 92, hill: 0.94, color: PALETTE.teal, dash: "7 4" },
  { id: "AB-03", ec50: 7.8, max: 82, hill: 1.14, color: PALETTE.amber, dash: "3 4" },
  { id: "AB-04", ec50: 15.2, max: 68, hill: 0.82, color: PALETTE.violet, dash: "10 4 2 4" },
];

const bindingX = (logConcentration) => 67 + ((logConcentration + 2) / 4) * 344;
const bindingY = (response) => 311 - (response / 100) * 236;

const bindingResponse = (antibody, concentration) =>
  antibody.max /
  (1 + Math.pow(antibody.ec50 / concentration, antibody.hill));

const bindingPoints = (antibody) =>
  Array.from({ length: 33 }, (_, index) => {
    const logConcentration = -2 + (index / 32) * 4;
    const concentration = Math.pow(10, logConcentration);
    return [
      bindingX(logConcentration),
      bindingY(bindingResponse(antibody, concentration)),
    ];
  });

const smoothPath = (points) => {
  if (!points.length) return "";
  return points.reduce((path, point, index) => {
    if (index === 0) return `M${point[0].toFixed(2)} ${point[1].toFixed(2)}`;
    const previous = points[index - 1];
    const beforePrevious = points[index - 2] || previous;
    const next = points[index + 1] || point;
    const controlOne = [
      previous[0] + (point[0] - beforePrevious[0]) / 6,
      previous[1] + (point[1] - beforePrevious[1]) / 6,
    ];
    const controlTwo = [
      point[0] - (next[0] - previous[0]) / 6,
      point[1] - (next[1] - previous[1]) / 6,
    ];
    return `${path}C${controlOne[0].toFixed(2)} ${controlOne[1].toFixed(2)} ${controlTwo[0].toFixed(2)} ${controlTwo[1].toFixed(2)} ${point[0].toFixed(2)} ${point[1].toFixed(2)}`;
  }, "");
};

export function AntibodyBindingVisual({ className = "" }) {
  const reducedMotion = useReducedMotion();
  const xTicks = [0.01, 0.1, 1, 10, 100];
  const yTicks = [0, 25, 50, 75, 100];

  return (
    <svg
      viewBox="0 0 640 400"
      className={`${visualClassName} ${className}`}
      role="img"
      aria-label="Antibody binding dose-response curves for four candidates with EC50 and maximum-response ranking"
      focusable="false"
    >
      <rect x="1" y="1" width="638" height="398" rx="24" className="fill-[#f7faff] stroke-slate-300 dark:fill-[#07090d] dark:stroke-white/[0.12]" />
      <text x="28" y="29" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">BINDING RESPONSE / 4 CANDIDATES</text>
      <text x="612" y="29" textAnchor="end" {...monoValue} fill={PALETTE.blue}>AB-01 · LEAD</text>

      <rect x="28" y="48" width="400" height="304" rx="16" className="fill-white stroke-slate-300 dark:fill-[#0a0f16] dark:stroke-white/[0.1]" />
      <rect x="444" y="48" width="168" height="304" rx="16" className="fill-white stroke-slate-300 dark:fill-[#0a0f16] dark:stroke-white/[0.1]" />

      {yTicks.map((tick) => {
        const y = bindingY(tick);
        return (
          <g key={tick} className="text-slate-400 dark:text-slate-500">
            <path d={`M67 ${y}H411`} stroke="currentColor" strokeWidth="1" strokeDasharray={tick === 0 ? undefined : "2 5"} />
            <text x="58" y={y + 3} textAnchor="end" {...monoLabel} fill="currentColor">{tick}</text>
          </g>
        );
      })}
      {xTicks.map((tick) => {
        const x = bindingX(Math.log10(tick));
        return (
          <g key={tick} className="text-slate-400 dark:text-slate-500">
            <path d={`M${x} 75V311`} stroke="currentColor" strokeWidth="1" strokeDasharray="2 5" />
            <text x={x} y="330" textAnchor="middle" {...monoLabel} fill="currentColor">{tick}</text>
          </g>
        );
      })}
      <text x="37" y="196" transform="rotate(-90 37 196)" textAnchor="middle" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">RESPONSE / %</text>
      <text x="239" y="345" textAnchor="middle" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">ANTIBODY CONCENTRATION / nM · LOG SCALE</text>

      <path d={`M67 ${bindingY(80)}H411`} stroke={PALETTE.teal} strokeOpacity="0.42" strokeDasharray="5 5" />
      <text x="407" y={bindingY(80) - 7} textAnchor="end" {...monoLabel} fill={PALETTE.teal}>80% RESPONSE</text>

      {ANTIBODIES.map((antibody, index) => {
        const points = bindingPoints(antibody);
        const sampleConcentrations = [0.03, 0.3, 3, 30];
        return (
          <g key={antibody.id}>
            <motion.path
              d={smoothPath(points)}
              fill="none"
              stroke={antibody.color}
              strokeWidth={index === 0 ? 3 : 2}
              strokeDasharray={antibody.dash}
              strokeLinecap="round"
              strokeLinejoin="round"
              {...drawIn(reducedMotion, 0.12 + index * 0.12, 0.9)}
            />
            {sampleConcentrations.map((concentration, pointIndex) => {
              const cx = bindingX(Math.log10(concentration));
              const cy = bindingY(bindingResponse(antibody, concentration));
              return (
                <motion.circle
                  key={concentration}
                  cx={cx}
                  cy={cy}
                  r={index === 0 ? 3.1 : 2.5}
                  fill={antibody.color}
                  stroke="white"
                  strokeOpacity="0.8"
                  strokeWidth="1"
                  initial={reducedMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: reducedMotion ? 0 : 0.3,
                    delay: reducedMotion ? 0 : 0.58 + index * 0.1 + pointIndex * 0.04,
                  }}
                />
              );
            })}
          </g>
        );
      })}

      <motion.g {...fadeIn(reducedMotion, 0.82, 0)}>
        <path
          d={`M${bindingX(Math.log10(ANTIBODIES[0].ec50))} ${bindingY(50)}V311`}
          stroke={PALETTE.blue}
          strokeOpacity="0.68"
          strokeDasharray="3 4"
        />
        <rect x="129" y="275" width="92" height="22" rx="11" fill={PALETTE.blue} fillOpacity="0.1" stroke={PALETTE.blue} strokeOpacity="0.32" />
        <text x="175" y="289" textAnchor="middle" {...monoLabel} fill={PALETTE.blue}>EC50 0.32 nM</text>
      </motion.g>

      <text x="462" y="69" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">RESPONSE</text>
      <text x="462" y="81" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">RANK</text>
      <text x="593" y="69" textAnchor="end" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">EC50 / nM</text>
      <text x="593" y="81" textAnchor="end" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">MAX / %</text>
      {ANTIBODIES.map((antibody, index) => (
        <motion.g key={antibody.id} transform={`translate(462 ${104 + index * 52})`} {...fadeOpacity(reducedMotion, 0.34 + index * 0.09)}>
          <circle cx="10" cy="8" r="10" fill={antibody.color} fillOpacity="0.12" stroke={antibody.color} strokeOpacity="0.42" />
          <text x="10" y="11" textAnchor="middle" {...monoValue} fill={antibody.color}>{index + 1}</text>
          <text x="31" y="6" {...monoValue} className="fill-slate-900 dark:fill-white">{antibody.id}</text>
          <text x="31" y="22" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">{index === 0 ? "LEAD" : index === 1 ? "BACKUP" : "REVIEW"}</text>
          <text x="131" y="6" textAnchor="end" {...monoValue} fill={antibody.color}>{antibody.ec50.toFixed(2)}</text>
          <text x="131" y="22" textAnchor="end" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">{antibody.max}%</text>
          <path d="M0 36H132" className="stroke-slate-200 dark:stroke-white/[0.08]" />
        </motion.g>
      ))}

      <text x="28" y="378" {...monoLabel} className="fill-slate-500 dark:fill-slate-400">DOSE RESPONSE · EC50 · MAXIMUM RESPONSE · CANDIDATE RANK</text>
      <text x="612" y="378" textAnchor="end" {...monoLabel} fill={PALETTE.blue}>READ 450 nm</text>
    </svg>
  );
}
